import { CaretDownIcon, CaretRightIcon, CopyIcon, InfoIcon } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import {
    ScoopAccordion,
    ScoopAccordionSummary,
    ScoopAccordionDetails,
    ScoopButton,
    ScoopCheckbox,
    ScoopTextField,
    ScoopText,
    ScoopDialog,
    ScoopTooltip,
    ScoopRow,
    ScoopColumn,
    useToast,
} from '../../../../ui'
import styles from './ConfigDialog.module.scss'

/**
 * Dialog for configuring dataset properties
 * Compatible with Modal Manager pattern
 *
 * @example
 * // Via Modal Manager
 * const configModal = useModal(MODALS.SOURCE.CONFIGURE)
 * const result = await configModal.show({
 *     inbox: inboxData,
 *     isEditing: true,
 *     server: serverInstance,
 *     refreshInbox: handleRefresh
 * })
 *
 * @example
 * // Direct usage (legacy support)
 * <ConfigDialog
 *     open={showDialog}
 *     onClose={() => setShowDialog(false)}
 *     inbox={inbox}
 *     isEditing={true}
 * />
 */
export const ConfigDialog = ({
    open,
    onClose,
    onConfirm,
    inbox,
    isEditing,
    goToAdvance,
    server,
    refreshInbox,
    isTransactional,
    isEmailSource,
    zIndex,
}) => {
    const [datasetName, setDatasetName] = useState('')
    const [datasetDescription, setDatasetDescription] = useState('')
    const [isMostRecent, setIsMostRecent] = useState(false)
    const [isIncremental, setIsIncremental] = useState(false)
    const [isMultipleLoads, setIsMultipleLoads] = useState(false)
    const [workbookRange, setWorkbookRange] = useState(false)
    const [expanded, setExpanded] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [generatedEmail, setGeneratedEmail] = useState('')
    const toast = useToast()

    useEffect(() => {
        if (!inbox) return
        setDatasetName(inbox?.label)
        setDatasetDescription(inbox?.description)
        setIsMostRecent(inbox?.keepOnlyCurrent)
        setIsIncremental(inbox?.incremental)
        setIsMultipleLoads(inbox?.enableMultipleSameDayLoads)
        setWorkbookRange(inbox.workbookRange ? inbox.workbookRange : '')
        isEmailSource && setGeneratedEmail(extractEmailFromInboxName(inbox?.inboxName) || '')
    }, [inbox, isEmailSource])

    const handleSave = async () => {
        setIsLoading(true)
        const saveInbox = getToSaveInbox()
        try {
            await server.postData(
                {
                    action: 'putObject',
                    class: 'scoop.ingest.ReportInbox',
                    value: saveInbox,
                },
                afterSaveCallback
            )
        } catch (e) {
            console.error(e)
        } finally {
            setIsLoading(false)
        }
    }

    const getToSaveInbox = () => {
        const toSaveInbox = { ...inbox }
        toSaveInbox.tables = undefined
        toSaveInbox.label = datasetName
        toSaveInbox.description = datasetDescription
        toSaveInbox.keepOnlyCurrent = isMostRecent
        toSaveInbox.incremental = isIncremental
        toSaveInbox.enableMultipleSameDayLoads = isMultipleLoads
        toSaveInbox.workbookRange = workbookRange
        return toSaveInbox
    }

    const afterSaveCallback = async (result) => {
        if (result && result.result) {
            await refreshInbox()
            onConfirm?.(true)
            handleClose()
        }
    }

    const handleClose = () => {
        resetState()
        onClose?.()
    }

    const resetState = () => {
        setDatasetName('')
        setDatasetDescription('')
        setIsMostRecent(false)
        setIsIncremental(false)
        setIsMultipleLoads(false)
    }

    const extractEmailFromInboxName = (inboxName) => {
        return inboxName + '@scoopup.report'
    }

    return (
        <>
            <ScoopDialog
                maxWidth="md"
                title={
                    <ScoopText className={styles.title}>
                        {isEditing ? 'Scoop Dataset Properties' : 'Create New Dataset'}
                    </ScoopText>
                }
                open={open}
                onClose={handleClose}
                zIndex={zIndex}
                actions={
                    <>
                        <ScoopButton variant="grey" onClick={handleClose}>
                            Cancel
                        </ScoopButton>
                        <ScoopButton
                            disabled={isLoading}
                            onClick={() => handleSave()}
                            loading={isLoading}
                        >
                            Save
                        </ScoopButton>
                    </>
                }
            >
                <ScoopColumn className={styles.container}>
                    <ScoopTextField
                        value={datasetName}
                        onChange={(e) => setDatasetName(e.target.value)}
                        label={'Dataset name'}
                        padding={'4px 14px'}
                    />
                    <ScoopTextField
                        value={datasetDescription}
                        label={'Dataset description (optional)'}
                        multiline
                        rows={4}
                        onChange={(e) => setDatasetDescription(e.target.value)}
                    />
                    {isEmailSource && (
                        <ScoopColumn className={styles.emailSection}>
                            <ScoopText className={styles.emailTitle}>
                                Forward the email to the following inbox
                            </ScoopText>
                            <ScoopRow className={styles.emailBox}>
                                {generatedEmail}
                                <div
                                    className={styles.copyIcon}
                                    onClick={() => {
                                        navigator.clipboard.writeText(generatedEmail).then(() => {
                                            toast.success('Email copied to clipboard')
                                        })
                                    }}
                                >
                                    <CopyIcon size={20} />
                                </div>
                            </ScoopRow>
                        </ScoopColumn>
                    )}
                    {isEditing && (
                        <ScoopRow className={styles.datasetTypeRow}>
                            <ScoopText className={styles.datasetTypeLabel}>Dataset Type:</ScoopText>
                            <ScoopRow className={styles.datasetTypeValueRow}>
                                <ScoopText className={styles.datasetTypeValue}>
                                    {isTransactional ? 'Transactional' : 'Snapshot'}
                                </ScoopText>
                                <ScoopTooltip
                                    title={
                                        isTransactional
                                            ? 'Accept all new reports as transactions, i.e. as additional records to the ' +
                                              'existing database. All records are cumulative.'
                                            : 'Treat each report as a new snapshot of an existing set of items, ' +
                                              'i.e. each report lists the current state of a set of objects. The unique key in each report ' +
                                              'is used to track changes for each item. Snapshots allow tracking of ' +
                                              'individual items and their state over time.'
                                    }
                                >
                                    <InfoIcon size={16} />
                                </ScoopTooltip>
                            </ScoopRow>
                        </ScoopRow>
                    )}
                    {isEditing ? (
                        <BasicConfig
                            isMostRecent={isMostRecent}
                            setIsMostRecent={setIsMostRecent}
                            isIncremental={isIncremental}
                            setIsIncremental={setIsIncremental}
                            isMultipleLoads={isMultipleLoads}
                            setIsMultipleLoads={setIsMultipleLoads}
                            isTransactional={isTransactional}
                            workbookRange={workbookRange}
                            setWorkbookRange={setWorkbookRange}
                        />
                    ) : (
                        <AccordionConfig
                            isMostRecent={isMostRecent}
                            setIsMostRecent={setIsMostRecent}
                            isIncremental={isIncremental}
                            setIsIncremental={setIsIncremental}
                            isMultipleLoads={isMultipleLoads}
                            setIsMultipleLoads={setIsMultipleLoads}
                            isTransactional={isTransactional}
                            workbookRange={workbookRange}
                            setWorkbookRange={setWorkbookRange}
                            expanded={expanded}
                            setExpanded={setExpanded}
                        />
                    )}
                    {isEditing && goToAdvance && (
                        <ScoopButton onClick={goToAdvance} variant="grey" size="xs">
                            Advanced
                        </ScoopButton>
                    )}
                </ScoopColumn>
            </ScoopDialog>
        </>
    )
}

const BasicConfig = ({
    isMostRecent,
    setIsMostRecent,
    isIncremental,
    setIsIncremental,
    isMultipleLoads,
    setIsMultipleLoads,
    workbookRange,
    setWorkbookRange,
    isTransactional,
    disableSpreadsheet,
}) => {
    return (
        <ScoopColumn className={styles.basicConfigContainer}>
            <ScoopColumn className={styles.configSection}>
                <ScoopText className={styles.configTitle}>Most recent only</ScoopText>
                <ScoopRow className={styles.configRow}>
                    <ScoopCheckbox
                        checked={isMostRecent}
                        onChange={() => setIsMostRecent(!isMostRecent)}
                    />
                    <ScoopText className={styles.configDescription}>
                        Scoop will retain only the data from the most recently loaded report.
                        Previous data is deleted.
                    </ScoopText>
                </ScoopRow>
            </ScoopColumn>
            {!isTransactional && (
                <ScoopColumn className={styles.configSection}>
                    <ScoopText className={styles.configTitle}>Incremental</ScoopText>
                    <ScoopRow className={styles.configRow}>
                        <ScoopCheckbox
                            checked={isIncremental}
                            onChange={() => setIsIncremental(!isIncremental)}
                        />
                        <ScoopText className={styles.configDescription}>
                            Scoop will interpret each new report as only the changed rows in the
                            snapshot. It will incorporate these rows with all the unchanged rows
                            from the prior snapshot.
                        </ScoopText>
                    </ScoopRow>
                </ScoopColumn>
            )}
            <ScoopColumn className={styles.configSection}>
                <ScoopText className={styles.configTitle}>Multiple loads per day</ScoopText>
                <ScoopRow className={styles.configRow}>
                    <ScoopCheckbox
                        checked={isMultipleLoads}
                        onChange={() => setIsMultipleLoads(!isMultipleLoads)}
                    />
                    <ScoopText className={styles.configDescription}>
                        Normally, if two reports are processed on the same day, Scoop retains only
                        the most recent. This allows all reports on a day to be retained - allowing
                        different parts of a dataset to be sent in different reports.
                    </ScoopText>
                </ScoopRow>
            </ScoopColumn>
            {!disableSpreadsheet && (
                <ScoopColumn className={styles.configSection}>
                    <ScoopText className={styles.configTitle}>Spreadsheet named range</ScoopText>
                    <ScoopText className={styles.configDescription}>
                        If you are sending a spreadsheet to this dataset, it is a best practice to
                        name the range within the spreadsheet to direct Scoop to the appropriate
                        data. If you are not loading a spreadsheet into this dataset, then this is
                        ignored.
                    </ScoopText>
                    <ScoopTextField
                        value={workbookRange}
                        onChange={(e) => {
                            setWorkbookRange(e.target.value)
                        }}
                    />
                </ScoopColumn>
            )}
        </ScoopColumn>
    )
}

export const AccordionConfig = ({
    isMostRecent,
    setIsMostRecent,
    isIncremental,
    setIsIncremental,
    isMultipleLoads,
    setIsMultipleLoads,
    isTransactional,
    workbookRange,
    setWorkbookRange,
    expanded,
    setExpanded,
    disableSpreadsheet,
}) => {
    return (
        <ScoopAccordion expanded={expanded} className={styles.accordion}>
            <ScoopAccordionSummary
                className={styles.accordionSummary}
                onClick={() => setExpanded(!expanded)}
                expandIcon={expanded ? <CaretDownIcon size={20} /> : <CaretRightIcon size={20} />}
            >
                <ScoopText className={styles.accordionTitle}>Extra Configuration</ScoopText>
            </ScoopAccordionSummary>
            <ScoopAccordionDetails className={styles.accordionDetails}>
                <BasicConfig
                    isMostRecent={isMostRecent}
                    setIsMostRecent={setIsMostRecent}
                    isIncremental={isIncremental}
                    setIsIncremental={setIsIncremental}
                    isMultipleLoads={isMultipleLoads}
                    setIsMultipleLoads={setIsMultipleLoads}
                    isTransactional={isTransactional}
                    disableSpreadsheet={disableSpreadsheet}
                    workbookRange={workbookRange}
                    setWorkbookRange={setWorkbookRange}
                />
            </ScoopAccordionDetails>
        </ScoopAccordion>
    )
}
